import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Users, Printer, AlertCircle, Clock, Download, CheckCircle, Edit2, Save, Trash2, UserCheck, DollarSign, Copy, BookOpen, GraduationCap, Loader2, Database, CheckSquare, X, RotateCcw } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useFirebaseRoster } from '../hooks/useFirebaseRoster';
import {
  createCharge,
  recordNoticeSent,
  getPendingCharges,
  getNoticeHistory,
  updateChargeStatus,
  clearNoticeHistory
} from '../services/chargeService';

function OdinsRavens() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const { SCHOOL_ROSTER, loading: rosterLoading, error: rosterError } = useFirebaseRoster();
  const [rawText, setRawText] = useState('');
  const [parsedNotices, setParsedNotices] = useState([]);
  const [noticeType, setNoticeType] = useState('initial');
  const [customDueDate, setCustomDueDate] = useState('');
  const [selectedNotices, setSelectedNotices] = useState([]);
  const [unmatchedPatrons, setUnmatchedPatrons] = useState([]);
  const [editingPatron, setEditingPatron] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState('all');
  const [organizeBy, setOrganizeBy] = useState('teacher');
  const [patronFilter, setPatronFilter] = useState('all');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [endOfYearDate, setEndOfYearDate] = useState('');
  const [noticeBreakdown, setNoticeBreakdown] = useState({
    student: { initial: [], final: [], toBeCharged: [], alreadyCharged: [] },
    parent: { initial: [], final: [], toBeCharged: [], alreadyCharged: [] },
    faculty: { reminder: [] }
  });
  const [chargeActionLoading, setChargeActionLoading] = useState(false);
  const [noticeActionLoading, setNoticeActionLoading] = useState(false);
  const [finalNoticeRecorded, setFinalNoticeRecorded] = useState(false);
  const [chargesRecorded, setChargesRecorded] = useState(false);
  const [firebaseCharges, setFirebaseCharges] = useState([]);
  const [chargeCheckComplete, setChargeCheckComplete] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  // Excluded students from notice lists (keyed by cleanName)
  const [excludedFromFinal, setExcludedFromFinal] = useState(new Set());
  const [excludedFromCharge, setExcludedFromCharge] = useState(new Set());

  // End of Year modal state — uses a separate paste set from the main flow
  const [showEoyModal, setShowEoyModal] = useState(false);
  const [eoyRawText, setEoyRawText] = useState('');
  const [eoyParsedNotices, setEoyParsedNotices] = useState([]);
  const [eoyParseLoading, setEoyParseLoading] = useState(false);
  const [eoyActionLoading, setEoyActionLoading] = useState(false);
  const [eoyChargesRecorded, setEoyChargesRecorded] = useState(false);
  const [eoyFinalRecorded, setEoyFinalRecorded] = useState(false);
  const [eoyOrganizeBy, setEoyOrganizeBy] = useState('teacher');
  const [eoyPatronFilter, setEoyPatronFilter] = useState('all');
  const [eoySelectedTeacher, setEoySelectedTeacher] = useState('all');

  // Check if roster has data
  const rosterIsEmpty = !rosterLoading && SCHOOL_ROSTER.students.length === 0 &&
                        SCHOOL_ROSTER.parents.length === 0 && SCHOOL_ROSTER.faculty.length === 0;

  // Load existing charges from Firebase on mount
  const loadFirebaseCharges = useCallback(async () => {
    if (!currentUser?.uid) {
      setChargeCheckComplete(true);
      return;
    }
    try {
      const charges = await getPendingCharges(currentUser?.uid);
      setFirebaseCharges(charges);
      setChargeCheckComplete(true);
    } catch (err) {
      console.error('Error loading Firebase charges:', err);
      setChargeCheckComplete(true);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    loadFirebaseCharges();
  }, [loadFirebaseCharges]);

  useEffect(() => {
    const today = new Date();
    const elevenWeeksFromNow = new Date(today.getTime() + (11 * 7 * 24 * 60 * 60 * 1000));
    setCustomDueDate(elevenWeeksFromNow.toISOString().split('T')[0]);

    // Set end of year date to May 31st of current or next year
    const currentYear = today.getFullYear();
    const mayThisYear = new Date(currentYear, 4, 31); // May 31st
    if (today > mayThisYear) {
      // If past May 31st, set to next year
      setEndOfYearDate(new Date(currentYear + 1, 4, 31).toISOString().split('T')[0]);
    } else {
      setEndOfYearDate(mayThisYear.toISOString().split('T')[0]);
    }
  }, []);

  const parseDueDate = (dueDateStr) => {
    try {
      const date = new Date(dueDateStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Calculate weeks overdue - used for display and minimum threshold only
  const calculateWeeksOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const daysSinceDue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
    return Math.floor(daysSinceDue / 7);
  };

  // Check if book is overdue at all (and meets minimum threshold for notices)
  // Minimum threshold: 4 weeks for parents, 4 weeks for students, 8 weeks for faculty
  const isOverdueForNotice = (dueDate, patronType) => {
    const weeksOverdue = calculateWeeksOverdue(dueDate);
    if (patronType === 'parent') {
      return weeksOverdue >= 4;
    } else if (patronType === 'faculty') {
      return weeksOverdue >= 8;
    } else {
      return weeksOverdue >= 2;
    }
  };

  // Calculate notice type based on weeks overdue
  // STUDENTS: initial (2-10 wks), final (11 wks), toBeCharged (12 wks), alreadyCharged (13+ wks)
  // PARENTS: initial (4-12 wks), final (13 wks), toBeCharged (14 wks), alreadyCharged (15+ wks)
  // FACULTY: reminder (8+ wks) - no charging
  const calculateNoticeType = (dueDate, patronType) => {
    if (!dueDate) return 'initial';

    const weeksSinceDue = calculateWeeksOverdue(dueDate);

    if (patronType === 'parent') {
      if (weeksSinceDue >= 15) {
        return 'alreadyCharged';
      } else if (weeksSinceDue === 14) {
        return 'toBeCharged';
      } else if (weeksSinceDue === 13) {
        return 'final';
      } else if (weeksSinceDue >= 4) {
        return 'initial';
      }
      return null;
    } else if (patronType === 'faculty') {
      if (weeksSinceDue >= 8) {
        return 'reminder';
      }
      return null;
    } else {
      // Students
      if (weeksSinceDue >= 13) {
        return 'alreadyCharged';
      } else if (weeksSinceDue === 12) {
        return 'toBeCharged';
      } else if (weeksSinceDue === 11) {
        return 'final';
      } else if (weeksSinceDue >= 2) {
        return 'initial';
      }
      return null;
    }
  };

  const parseOverdueText = async () => {
    if (!rawText.trim()) {
      alert('Please paste the overdue notices text first');
      return;
    }

    if (rosterIsEmpty) {
      alert('No roster data found! Please import your roster data in Urd\'s Well first.');
      return;
    }

    setParseLoading(true);

    try {
    // Reset recording states for new parse
    setFinalNoticeRecorded(false);
    setChargesRecorded(false);

    const noticeBlocks = rawText.split('Overdue Notice').filter(block => block.trim());
    const notices = {};

    noticeBlocks.forEach(block => {
      const lines = block.trim().split('\n');
      let patronName = lines[0].trim();
      patronName = patronName.replace(/\s*"[^"]+"\s*/g, ' ').trim();

      let cleanPatronName = patronName;

      if (cleanPatronName.toLowerCase().startsWith('sr.,')) {
        cleanPatronName = cleanPatronName.replace(/^sr\.,\s*/i, 'Sr. ');
      }

      if (cleanPatronName.toLowerCase().endsWith(' sr.')) {
        const nameWithoutSr = cleanPatronName.replace(/\s+sr\.$/i, '').trim();
        cleanPatronName = `Sr. ${nameWithoutSr}`;
      }

      let altPatronName = cleanPatronName;

      if (!cleanPatronName.includes(',')) {
        const parts = cleanPatronName.split(' ').filter(p => p);
        if (parts.length >= 2) {
          if (parts[0].toLowerCase() === 'sr.' || parts[0].toLowerCase() === 'sr') {
            altPatronName = cleanPatronName;
          } else {
            const lastName = parts[parts.length - 1];
            const firstName = parts.slice(0, -1).join(' ');
            altPatronName = `${lastName}, ${firstName}`;
          }
        }
      } else {
        const nameParts = cleanPatronName.split(',').map(p => p.trim());
        if (nameParts.length === 2) {
          const firstNameParts = nameParts[1].split(' ').map(p => p.trim()).filter(p => p);
          if (firstNameParts.length > 1) {
            const firstNames = firstNameParts.slice(0, -1).join(' ');
            const lastWord = firstNameParts[firstNameParts.length - 1];
            altPatronName = `${lastWord} ${nameParts[0]}, ${firstNames}`;
          }
        }
      }

      let patronType = SCHOOL_ROSTER.getPatronType(cleanPatronName);
      if (patronType === 'unknown' && altPatronName !== cleanPatronName) {
        patronType = SCHOOL_ROSTER.getPatronType(altPatronName);
        if (patronType !== 'unknown') {
          cleanPatronName = altPatronName;
        }
      }

      const numberMatch = block.match(/Number:\s*(\d+)/);
      const patronNumber = numberMatch ? numberMatch[1] : '';

      const titleMatch = block.match(/Title:\s*(.+)/);
      const callNumberMatch = block.match(/Call Number:\s*(.+)/);
      const barcodeMatch = block.match(/Bar Code:\s*(.+)/);
      const dueDateMatch = block.match(/Due Date:\s*(.+)/);
      const costMatch = block.match(/Cost if lost:\s*(.+)/);

      const dueDateStr = dueDateMatch ? dueDateMatch[1].trim() : '';
      const dueDate = parseDueDate(dueDateStr);

      // Only include if overdue and meets minimum threshold
      if (!isOverdueForNotice(dueDate, patronType)) return;

      const weeksOverdue = calculateWeeksOverdue(dueDate);

      const book = {
        title: titleMatch ? titleMatch[1].trim() : 'Unknown Title',
        callNumber: callNumberMatch ? callNumberMatch[1].trim() : '',
        barcode: barcodeMatch ? barcodeMatch[1].trim() : '',
        dueDate: dueDateStr,
        dueDateObj: dueDate,
        cost: costMatch ? costMatch[1].trim() : '$0.00',
        weeksOverdue
      };

      const nameKey = cleanPatronName;

      if (!notices[nameKey]) {
        notices[nameKey] = {
          name: cleanPatronName,
          originalName: patronName,
          number: patronNumber,
          patronType: patronType,
          books: []
        };
      }
      notices[nameKey].books.push(book);
    });

    const allNotices = [];

    Object.values(notices).forEach(patronData => {
      let cleanName = patronData.name;
      let matchedInfo = null;
      const patronType = patronData.patronType;

      if (patronType === 'parent') {
        matchedInfo = SCHOOL_ROSTER.getParent(cleanName);
        if (matchedInfo && matchedInfo.children.length === 0) {
          return;
        }
      } else if (patronType === 'faculty') {
        matchedInfo = SCHOOL_ROSTER.getFaculty(cleanName);
      } else if (patronType === 'student') {
        matchedInfo = SCHOOL_ROSTER.getTeacher(cleanName);
      } else {
        if (!cleanName.includes(',')) {
          const parts = cleanName.split(' ');
          if (parts.length >= 2) {
            for (let i = 1; i <= parts.length - 1 && !matchedInfo; i++) {
              const firstName = parts.slice(0, -i).join(' ');
              const lastName = parts.slice(-i).join(' ');
              const formatToTry = `${lastName}, ${firstName}`;
              matchedInfo = SCHOOL_ROSTER.getTeacher(formatToTry);
              if (matchedInfo) {
                cleanName = formatToTry;
                break;
              }
            }
          }
        } else {
          matchedInfo = SCHOOL_ROSTER.getTeacher(cleanName);
        }
      }

      // Calculate oldest due date and max weeks overdue
      const oldestDueDate = patronData.books
        .map(b => b.dueDateObj)
        .filter(d => d !== null)
        .sort((a, b) => a - b)[0];

      const maxWeeksOverdue = Math.max(...patronData.books.map(b => b.weeksOverdue));

      const totalCost = patronData.books.reduce((sum, book) => {
        const cost = parseFloat(book.cost.replace('$', '').replace(',', ''));
        return sum + (isNaN(cost) ? 0 : cost);
      }, 0);

      const notice = {
        name: patronData.name,
        number: patronData.number,
        cleanName: cleanName,
        patronType: patronType,
        matched: !!matchedInfo,
        oldestDueDate,
        maxWeeksOverdue,
        books: patronData.books,
        totalCost,
        noticeId: `${cleanName}-${patronType}`
      };

      if (patronType === 'parent' && matchedInfo) {
        notice.children = matchedInfo.children;
        notice.teacher = matchedInfo.teacher;
        notice.grade = matchedInfo.grade;
      } else if (patronType === 'faculty' && matchedInfo) {
        notice.grade = matchedInfo.grade;
        notice.teacher = matchedInfo.name;
      } else if (matchedInfo) {
        notice.teacher = matchedInfo.teacher || '';
        notice.grade = matchedInfo.grade || '';
      }

      allNotices.push(notice);
    });

    const matched = allNotices.filter(n => n.matched);
    const unmatched = allNotices.filter(n => !n.matched);

    const breakdown = {
      student: { initial: [], final: [], toBeCharged: [], alreadyCharged: [] },
      parent: { initial: [], final: [], toBeCharged: [], alreadyCharged: [] },
      faculty: { reminder: [] }
    };

    // Categorize notices based on Firebase history
    // Priority: 1) Already charged → 2) Has final notice on record → 3) Needs final notice first
    const categorizedNotices = await Promise.all(
      matched.map(async (notice) => {
        // Faculty always gets reminder, no charging
        if (notice.patronType === 'faculty') {
          return { ...notice, suggestedNoticeType: 'reminder' };
        }

        // Check if patron has pending charge in Firebase
        const pendingCharges = firebaseCharges.filter(c =>
          c.patronName === notice.cleanName && c.status === 'pending'
        );
        if (pendingCharges.length > 0) {
          return { ...notice, suggestedNoticeType: 'alreadyCharged', pendingCharge: pendingCharges[0] };
        }

        // Calculate weeks-based notice type for minimum threshold
        const weeksBasedType = calculateNoticeType(notice.oldestDueDate, notice.patronType);

        // If not yet at final notice threshold, use weeks-based type
        if (weeksBasedType === 'initial' || weeksBasedType === null) {
          return { ...notice, suggestedNoticeType: weeksBasedType || 'initial' };
        }

        // At or past final notice threshold - check Firebase history
        const noticeHistory = await getNoticeHistory(currentUser?.uid, notice.cleanName);
        const hasFinalNotice = noticeHistory.some(h => h.noticeType === 'final');

        if (hasFinalNotice) {
          // They've been warned before, ready to charge
          return { ...notice, suggestedNoticeType: 'toBeCharged', hasFinalOnRecord: true };
        } else {
          // No final notice on record - send final notice first (even if 20 weeks overdue)
          return { ...notice, suggestedNoticeType: 'final', hasFinalOnRecord: false };
        }
      })
    );

    // Sort into breakdown categories
    categorizedNotices.forEach(notice => {
      if (notice.patronType === 'faculty') {
        breakdown.faculty.reminder.push(notice);
      } else if (notice.patronType === 'student') {
        if (breakdown.student[notice.suggestedNoticeType]) {
          breakdown.student[notice.suggestedNoticeType].push(notice);
        }
      } else if (notice.patronType === 'parent') {
        if (breakdown.parent[notice.suggestedNoticeType]) {
          breakdown.parent[notice.suggestedNoticeType].push(notice);
        }
      }
    });

    // Filter out alreadyCharged from active notices (they're already being handled)
    const activeNotices = categorizedNotices.filter(n => n.suggestedNoticeType !== 'alreadyCharged');

    setParsedNotices(activeNotices);
    setUnmatchedPatrons(unmatched);
    setNoticeBreakdown(breakdown);
    setSelectedNotices(activeNotices);
    } catch (err) {
      console.error('Error parsing notices:', err);
      alert('Error parsing notices. Please check the console for details.');
    } finally {
      setParseLoading(false);
    }
  };

 const savePatronEdit = (patron, newTeacher, newGrade) => {
  // Calculate notice type based on max weeks overdue
  const noticeType = calculateNoticeType(patron.oldestDueDate, patron.patronType) || 'initial';

  const updatedPatron = {
    ...patron,
    teacher: newTeacher,
    grade: newGrade,
    matched: true,
    suggestedNoticeType: noticeType
  };

  setUnmatchedPatrons(prev => prev.filter(p => p.noticeId !== patron.noticeId));
  setParsedNotices(prev => [...prev, updatedPatron]);

  const patronTypeKey = updatedPatron.patronType;
  const type = updatedPatron.suggestedNoticeType;

  // Update breakdown with category structure
  if (patronTypeKey === 'student' || patronTypeKey === 'parent') {
    if (['initial', 'final', 'toBeCharged', 'alreadyCharged'].includes(type)) {
      setNoticeBreakdown(prev => ({
        ...prev,
        [patronTypeKey]: {
          ...prev[patronTypeKey],
          [type]: [...prev[patronTypeKey][type], updatedPatron]
        }
      }));
    }
  } else if (patronTypeKey === 'faculty') {
    setNoticeBreakdown(prev => ({
      ...prev,
      faculty: {
        reminder: [...prev.faculty.reminder, updatedPatron]
      }
    }));
  }

  if (type !== 'alreadyCharged') {
    setSelectedNotices(prev => [...prev, updatedPatron]);
  }

  setEditingPatron(null);
};

  const deleteUnmatchedPatron = (patron) => {
    setUnmatchedPatrons(prev => prev.filter(p => p.noticeId !== patron.noticeId));
  };

  // Parse the End-of-Year tab-separated paste:
  // Last, First \t PatronID \t (blank) \t Title \t CallNumber \t Barcode \t CheckoutDate \t DueDate \t Price
  const parseEoyOverdueText = async () => {
    if (!eoyRawText.trim()) {
      alert('Please paste the end-of-year overdue list first');
      return;
    }
    if (rosterIsEmpty) {
      alert('No roster data found! Please import your roster data in Urd\'s Well first.');
      return;
    }

    setEoyParseLoading(true);
    setEoyChargesRecorded(false);
    setEoyFinalRecorded(false);

    try {
      const lines = eoyRawText.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim());
      const groupedByPatron = {};

      for (const line of lines) {
        const parts = line.split('\t');
        if (parts.length < 8) continue;

        const patronName = (parts[0] || '').trim();
        const patronId = (parts[1] || '').trim();
        const title = (parts[3] || '').trim();
        const callNumber = (parts[4] || '').trim();
        const barcode = (parts[5] || '').trim();
        const dueDateStr = (parts[7] || '').trim();
        const rawCost = (parts[8] || '').trim();

        if (!patronName) continue;

        const dueDate = parseDueDate(dueDateStr);
        const weeksOverdue = calculateWeeksOverdue(dueDate);
        const cost = rawCost
          ? (rawCost.startsWith('$') ? rawCost : `$${rawCost}`)
          : '$0.00';

        const book = {
          title: title || 'Unknown Title',
          callNumber,
          barcode,
          dueDate: dueDateStr,
          dueDateObj: dueDate,
          cost,
          weeksOverdue
        };

        let cleanName = patronName;
        let patronType = SCHOOL_ROSTER.getPatronType(cleanName);

        if (patronType === 'unknown' && cleanName.includes(',')) {
          const nameParts = cleanName.split(',').map(p => p.trim());
          if (nameParts.length === 2) {
            const firstNameParts = nameParts[1].split(' ').map(p => p.trim()).filter(Boolean);
            if (firstNameParts.length > 1) {
              const firstNames = firstNameParts.slice(0, -1).join(' ');
              const lastWord = firstNameParts[firstNameParts.length - 1];
              const altName = `${lastWord} ${nameParts[0]}, ${firstNames}`;
              const altType = SCHOOL_ROSTER.getPatronType(altName);
              if (altType !== 'unknown') {
                cleanName = altName;
                patronType = altType;
              }
            }
          }
        }

        if (!groupedByPatron[cleanName]) {
          groupedByPatron[cleanName] = {
            originalName: patronName,
            number: patronId,
            patronType,
            books: []
          };
        }
        groupedByPatron[cleanName].books.push(book);
      }

      const allNotices = [];
      Object.entries(groupedByPatron).forEach(([cleanName, patronData]) => {
        let matchedInfo = null;
        const patronType = patronData.patronType;

        if (patronType === 'parent') {
          matchedInfo = SCHOOL_ROSTER.getParent(cleanName);
          if (matchedInfo && matchedInfo.children.length === 0) return;
        } else if (patronType === 'faculty') {
          matchedInfo = SCHOOL_ROSTER.getFaculty(cleanName);
        } else if (patronType === 'student') {
          matchedInfo = SCHOOL_ROSTER.getTeacher(cleanName);
        }

        const oldestDueDate = patronData.books
          .map(b => b.dueDateObj)
          .filter(d => d !== null)
          .sort((a, b) => a - b)[0];
        const maxWeeksOverdue = Math.max(...patronData.books.map(b => b.weeksOverdue));
        const totalCost = patronData.books.reduce((sum, b) => {
          const c = parseFloat(String(b.cost).replace('$', '').replace(',', ''));
          return sum + (isNaN(c) ? 0 : c);
        }, 0);

        const notice = {
          name: patronData.originalName,
          number: patronData.number,
          cleanName,
          patronType,
          matched: !!matchedInfo,
          oldestDueDate,
          maxWeeksOverdue,
          books: patronData.books,
          totalCost,
          noticeId: `${cleanName}-${patronType}-eoy`
        };

        if (patronType === 'parent' && matchedInfo) {
          notice.children = matchedInfo.children;
          notice.teacher = matchedInfo.teacher;
          notice.grade = matchedInfo.grade;
        } else if (patronType === 'faculty' && matchedInfo) {
          notice.grade = matchedInfo.grade;
          notice.teacher = matchedInfo.name;
        } else if (matchedInfo) {
          notice.teacher = matchedInfo.teacher || '';
          notice.grade = matchedInfo.grade || '';
        }

        allNotices.push(notice);
      });

      setEoyParsedNotices(allNotices);
    } catch (err) {
      console.error('Error parsing EOY paste:', err);
      alert('Failed to parse paste. Check the format and try again.');
    } finally {
      setEoyParseLoading(false);
    }
  };

  const eoyFilteredNotices = (() => {
    let filtered = eoyParsedNotices.filter(n => n.matched);
    if (eoyPatronFilter !== 'all') {
      filtered = filtered.filter(n => n.patronType === eoyPatronFilter);
    }
    if (eoySelectedTeacher !== 'all') {
      filtered = filtered.filter(n => n.teacher === eoySelectedTeacher);
    }
    return filtered;
  })();

  const getEoyOrganizedNotices = (organizationType = eoyOrganizeBy) => {
    const organized = {};
    const list = eoyFilteredNotices;
    if (organizationType === 'teacher') {
      list.forEach(n => {
        const k = n.teacher || 'No Teacher';
        if (!organized[k]) organized[k] = [];
        organized[k].push(n);
      });
    } else if (organizationType === 'grade') {
      list.forEach(n => {
        const k = n.grade || 'No Grade';
        if (!organized[k]) organized[k] = [];
        organized[k].push(n);
      });
    } else if (organizationType === 'grade-teacher') {
      list.forEach(n => {
        const k = `${n.grade || 'No Grade'} - ${n.teacher || 'No Teacher'}`;
        if (!organized[k]) organized[k] = [];
        organized[k].push(n);
      });
    } else if (organizationType === 'patron-type') {
      list.forEach(n => {
        const k = n.patronType || 'Unknown';
        if (!organized[k]) organized[k] = [];
        organized[k].push(n);
      });
    }
    return organized;
  };

  const getEoyUniqueTeachers = () => {
    const teachers = new Set(eoyParsedNotices.filter(n => n.matched).map(n => n.teacher).filter(Boolean));
    return Array.from(teachers).sort();
  };

  const eoyGenerateAndOutput = async (suggestedNoticeType, mode) => {
    const organized = getEoyOrganizedNotices();
    const sortedKeys = Object.keys(organized).sort();
    const ordered = [];
    sortedKeys.forEach(k => ordered.push(...organized[k]));

    if (ordered.length === 0) {
      alert('No notices to generate. Adjust filters or paste data first.');
      return;
    }

    const isFinal = suggestedNoticeType === 'endOfYearFinal';
    const isCharge = suggestedNoticeType === 'endOfYearCharge';
    const recordableNotices = ordered.filter(n => n.patronType !== 'faculty');

    if (isFinal && !eoyFinalRecorded && recordableNotices.length > 0) {
      const ok = window.confirm(
        `Generate and record ${recordableNotices.length} End-of-Year Final Notice(s)?\n\nThis writes the final notice to each patron's history in Firebase.`
      );
      if (!ok) return;
    } else if (isCharge && !eoyChargesRecorded && recordableNotices.length > 0) {
      const total = recordableNotices.reduce((s, n) => s + n.totalCost, 0);
      const ok = window.confirm(
        `Generate and record ${recordableNotices.length} End-of-Year Charge(s) totaling $${total.toFixed(2)}?\n\nThis writes pending charges to Firebase.`
      );
      if (!ok) return;
    }

    setEoyActionLoading(true);
    try {
      if (isFinal && !eoyFinalRecorded) {
        for (const notice of recordableNotices) {
          await recordNoticeSent(
            currentUser?.uid,
            notice.cleanName,
            notice.patronType,
            'final',
            notice.books.map(b => ({
              title: b.title,
              barcode: b.barcode,
              dueDate: b.dueDate
            }))
          );
        }
        setEoyFinalRecorded(true);
      } else if (isCharge && !eoyChargesRecorded) {
        for (const notice of recordableNotices) {
          await createCharge(currentUser?.uid, {
            patronName: notice.cleanName,
            patronType: notice.patronType,
            books: notice.books.map(b => ({
              title: b.title,
              barcode: b.barcode,
              cost: parseFloat(String(b.cost).replace('$', '').replace(',', '')) || 0,
              dueDate: b.dueDate
            })),
            totalCost: notice.totalCost,
            notes: `End-of-year charge via Odin's Ravens on ${new Date().toLocaleDateString()}`
          });
        }
        setEoyChargesRecorded(true);
        await loadFirebaseCharges();
      }
    } catch (err) {
      console.error('Error recording EOY notices:', err);
      alert('Failed to record to system. Please try again before printing/downloading.');
      setEoyActionLoading(false);
      return;
    }

    const extras = isFinal
      ? { suggestedNoticeType, endOfYearReturnDate: new Date(endOfYearDate).toLocaleDateString() }
      : { suggestedNoticeType };
    const notices = ordered.map(n => ({ ...n, ...extras }));
    const html = generateAllNoticesHTML(notices);

    if (mode === 'download') {
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      const label = isFinal ? 'END_OF_YEAR_FINAL' : 'END_OF_YEAR_CHARGE';
      a.href = url;
      a.download = `${label}_NOTICES_${date}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      } else {
        alert('Unable to open print window. Use download instead.');
      }
    }

    setEoyActionLoading(false);
  };

  const generateNotice = (notice) => {
    const noticeTypeToUse = notice.suggestedNoticeType || noticeType;
    const patronType = notice.patronType || 'student';
    
    let headerText = '';
    let messageText = '';
    
    const bookDetails = notice.books.map(book => {
      const originalDueDate = book.dueDateObj || new Date(book.dueDate);
      
      let adjustedDueDate = new Date(originalDueDate);
      let weeksOverdueFrom = originalDueDate;
      
      if (patronType === 'parent') {
        adjustedDueDate = new Date(originalDueDate.getTime() + (1 * 7 * 24 * 60 * 60 * 1000));
        weeksOverdueFrom = adjustedDueDate;
      } else if (patronType === 'faculty') {
        adjustedDueDate = new Date(originalDueDate.getTime() + (4 * 7 * 24 * 60 * 60 * 1000));
        weeksOverdueFrom = adjustedDueDate;
      } else {
        weeksOverdueFrom = originalDueDate;
      }
      
      let returnByDate = null;
if (patronType === 'parent') {
  returnByDate = new Date(originalDueDate.getTime() + (14 * 7 * 24 * 60 * 60 * 1000));
} else if (patronType !== 'faculty') {
  returnByDate = new Date(originalDueDate.getTime() + (11 * 7 * 24 * 60 * 60 * 1000));
}
      
      const today = new Date();
      const daysSinceDue = Math.floor((today - weeksOverdueFrom) / (1000 * 60 * 60 * 24));
      const weeksOverdue = Math.floor(daysSinceDue / 7);
      
      return {
        title: book.title,
        dueDate: adjustedDueDate.toLocaleDateString(),
        returnBy: returnByDate ? returnByDate.toLocaleDateString() : null,
        returnByObj: returnByDate,
        weeksOverdue: weeksOverdue
      };
    });
    
    if (patronType === 'parent') {
      switch(noticeTypeToUse) {
        case 'endOfYearFinal':
          headerText = '🚨 END OF YEAR FINAL NOTICE 🚨';
          messageText = `The school year is ending soon! All library materials must be returned or paid for before the end of the school year. Please return the following items by ${notice.endOfYearReturnDate || 'the last day of school'} to avoid charges. If items are not returned or payment is not received by this deadline, your FACTS account will be charged $${notice.totalCost.toFixed(2)} for replacement costs. This obligation must be settled before year-end records are finalized and before graduation requirements can be completed.`;
          break;
        case 'endOfYearCharge':
          headerText = '💳 END OF YEAR CHARGE NOTICE';
          messageText = `Your FACTS account will be charged $${notice.totalCost.toFixed(2)} for the following unreturned library items. All library obligations must be settled before year-end records are finalized and before graduation requirements can be completed. Once payment is received or items are returned, this obligation will be cleared. We understand things get misplaced in busy households—these books are valuable teaching resources we'd love to have back on our shelves for all students to enjoy.`;
          break;
        case 'final':
          headerText = '🚨 FINAL NOTICE 🚨';
          messageText = `We know how busy family life can be! These library books are important resources for all our children to enjoy. Please return the following items by the deadline shown to avoid charges. If items are not returned or payment is not received by the deadline, your FACTS account will be charged $${notice.totalCost.toFixed(2)} for replacement costs, and library checkout privileges will be temporarily suspended until the matter is resolved.`;
          break;
        case 'toBeCharged':
        case 'alreadyCharged':
          headerText = '💳 ACCOUNT CHARGED';
          messageText = `Your FACTS account has been charged $${notice.totalCost.toFixed(2)} for the following unreturned library items. We understand things get misplaced in busy households! Library checkout privileges are temporarily suspended. Once payment is received or items are returned, privileges will be restored immediately. These books are valuable teaching resources we'd love to have back on our shelves for all students to enjoy.`;
          break;
        default:
          headerText = '👪 OVERDUE NOTICE';
          messageText = `We know how busy life with children can be! These library books are wonderful resources that other students are waiting to enjoy. Please return the following items at your earliest convenience so we can keep our library collection available for all our Holy Family families. If you need more time, please contact us to arrange a renewal! If any book is not returned by the "Last Return Date Before Charging" listed below, your FACTS account will be automatically charged the replacement cost shown here. Charges are non-refundable once processed.`;
      }
    } else if (patronType === 'faculty') {
      headerText = '🍎 FRIENDLY REMINDER';
      messageText = `We hope these library materials have been helpful in your classroom! Please contact us at your earliest convenience to return or renew the following items. As a reminder, all library materials must be returned before end-of-year checklists can be completed. We're happy to work with you to arrange renewal or a convenient return time!`;
    } else {
      switch(noticeTypeToUse) {
        case 'endOfYearFinal':
          headerText = '🚨 END OF YEAR FINAL NOTICE 🚨';
          messageText = `The school year is ending soon! All library materials must be returned or paid for before the end of the school year. Please return the following items by ${notice.endOfYearReturnDate || 'the last day of school'} to avoid charges. If items are not returned or payment is not received by this deadline, your FACTS account will be charged $${notice.totalCost.toFixed(2)} for replacement costs. This obligation must be settled before year-end records are finalized and before graduation requirements can be completed.`;
          break;
        case 'endOfYearCharge':
          headerText = '💳 END OF YEAR CHARGE NOTICE';
          messageText = `Your FACTS account will be charged $${notice.totalCost.toFixed(2)} for the following unreturned library items. All library obligations must be settled before year-end records are finalized and before graduation requirements can be completed. Once payment is received or items are returned, this obligation will be cleared. We appreciate your prompt attention to this matter.`;
          break;
        case 'final':
          headerText = '🚨 FINAL NOTICE 🚨';
          messageText = `We hope you're enjoying your library books! Please return the following items by their deadlines to avoid charges. If items are not returned or payment is not received by the deadline shown, your FACTS account will be charged $${notice.totalCost.toFixed(2)} for replacement costs, and library checkout privileges will be temporarily suspended until the matter is resolved.`;
          break;
        case 'toBeCharged':
        case 'alreadyCharged':
          headerText = '💳 ACCOUNT CHARGED';
          messageText = `Your FACTS account has been charged $${notice.totalCost.toFixed(2)} for the following unreturned library items. Library checkout privileges are temporarily suspended. Once payment is received or items are returned, privileges will be restored immediately. We appreciate your prompt attention to this matter.`;
          break;
        default:
          headerText = '📚 OVERDUE NOTICE';
          messageText = `We hope you're enjoying your library books! Please return the following items as soon as possible. If you need more time, come renew your books at the library! If any book is not returned by the "Last Return Date Before Charging" listed below, your FACTS account will be automatically charged the replacement cost shown here. Charges are non-refundable once processed.`;
      }
    }
    
    return {
      ...notice,
      headerText,
      messageText,
      bookDetails,
      noticeTypeToUse,
      patronType
    };
  };

  const generateNoticeHTML = (notice) => {
    const enrichedNotice = generateNotice(notice);
    const noticeTypeToUse = enrichedNotice.noticeTypeToUse === 'alreadyCharged' ? 'toBeCharged' : enrichedNotice.noticeTypeToUse;
    const patronType = enrichedNotice.patronType || 'student';
    
    let colorScheme = {};
    if (patronType === 'parent') {
      colorScheme = {
        primary: '#7C3AED',
        secondary: '#A78BFA',
        background: '#EDE9FE',
        border: '#C4B5FD',
        accent: '#6D28D9'
      };
    } else if (patronType === 'faculty') {
      colorScheme = {
        primary: '#059669',
        secondary: '#34D399',
        background: '#D1FAE5',
        border: '#A7F3D0',
        accent: '#047857'
      };
    } else {
      colorScheme = {
        primary: '#1E40AF',
        secondary: '#3B82F6',
        background: '#DBEAFE',
        border: '#93C5FD',
        accent: '#0D9488'
      };
    }
    
    let patronInfoHTML = '';
    
    if (patronType === 'parent') {
      const childrenList = enrichedNotice.children ? enrichedNotice.children.join(', ') : 'N/A';
      patronInfoHTML = `
        <div style="background: ${colorScheme.background}; padding: 12px; border-radius: 6px; margin-bottom: 15px; border: 1px solid ${colorScheme.border};">
          <div style="font-size: 15px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 8px;">Parent Overdue Notice</div>
          <div style="display: grid; grid-template-columns: 1fr; gap: 6px; font-size: 13px;">
            <div style="display: flex;">
              <span style="font-weight: bold; width: 100px; color: #4B5563;">Parent:</span>
              <span style="color: #1F2937;">${enrichedNotice.cleanName}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 100px; color: #4B5563;">Children:</span>
              <span style="color: #1F2937;">${childrenList}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 100px; color: #4B5563;">Teacher:</span>
              <span style="color: #1F2937;">${enrichedNotice.teacher}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 100px; color: #4B5563;">Grade:</span>
              <span style="color: #1F2937;">${enrichedNotice.grade}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 100px; color: #4B5563;">Date:</span>
              <span style="color: #1F2937;">${new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      `;
    } else if (patronType === 'faculty') {
      patronInfoHTML = `
        <div style="background: ${colorScheme.background}; padding: 12px; border-radius: 6px; margin-bottom: 15px; border: 1px solid ${colorScheme.border};">
          <div style="font-size: 15px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 8px;">Faculty Reminder</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 13px;">
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Faculty:</span>
              <span style="color: #1F2937;">${enrichedNotice.cleanName}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Grade/Dept:</span>
              <span style="color: #1F2937;">${enrichedNotice.grade || 'N/A'}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Date:</span>
              <span style="color: #1F2937;">${new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      `;
    } else {
      patronInfoHTML = `
        <div style="background: ${colorScheme.background}; padding: 12px; border-radius: 6px; margin-bottom: 15px; border: 1px solid ${colorScheme.border};">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 13px;">
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Student:</span>
              <span style="color: #1F2937;">${enrichedNotice.cleanName}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Teacher:</span>
              <span style="color: #1F2937;">${enrichedNotice.teacher}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Grade:</span>
              <span style="color: #1F2937;">${enrichedNotice.grade}</span>
            </div>
            <div style="display: flex;">
              <span style="font-weight: bold; width: 75px; color: #4B5563;">Date:</span>
              <span style="color: #1F2937;">${new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      `;
    }
    
    const facultyPolicyHTML = patronType === 'faculty' ? `
      <div style="background: ${colorScheme.background}; border: 1px solid ${colorScheme.border}; border-radius: 6px; padding: 10px; margin: 12px 0; font-size: 11px;">
        <strong style="color: ${colorScheme.accent};">📋 End-of-Year Policy Reminder:</strong> All library materials must be returned before end-of-year checklists can be completed. Please contact us to arrange a convenient return time at vkahn@holyfamilycs.org.
      </div>
    ` : '';
    
    return `
      <div class="notice-page" style="page-break-after: always; page-break-inside: avoid;">
        <div style="font-family: Calibri, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.4; color: #333; padding: 20px; max-width: 7.5in; margin: 0 auto;">
          <div style="text-align: center; border-bottom: 3px solid ${colorScheme.primary}; padding-bottom: 6px; margin-bottom: 10px;">
            <div style="font-size: 18px; font-weight: bold; margin-bottom: 2px; color: ${colorScheme.primary}; letter-spacing: 0.5px;">Holy Family Catholic School</div>
            <div style="font-size: 13px; color: ${colorScheme.secondary}; margin-bottom: 2px; font-weight: 600;">HFCS Library</div>
            <div style="font-size: 10px; color: #666;">9400 Neenah Ave • Austin, TX 78717</div>
          </div>
          
          <div style="font-size: 20px; font-weight: bold; text-align: center; margin: 12px 0; padding: 10px; ${noticeTypeToUse === 'final' || noticeTypeToUse === 'endOfYearFinal' ? 'background: #FEE2E2; color: #991B1B; border: 2px solid #DC2626;' : ''} ${noticeTypeToUse === 'toBeCharged' || noticeTypeToUse === 'endOfYearCharge' ? 'background: #FECACA; color: #7F1D1D; border: 2px solid #B91C1C;' : ''} ${noticeTypeToUse === 'initial' || noticeTypeToUse === 'reminder' ? `background: ${colorScheme.background}; color: ${colorScheme.accent}; border: 2px solid ${colorScheme.primary};` : ''} border-radius: 6px;">
            ${enrichedNotice.headerText}
          </div>
          
          ${patronInfoHTML}
          
          <div style="margin-bottom: 12px;">
            <div style="font-size: 15px; font-weight: bold; margin-bottom: 8px; border-bottom: 2px solid ${colorScheme.primary}; padding-bottom: 4px; color: ${colorScheme.primary};">
              Overdue Items (${enrichedNotice.books.length})
            </div>
            ${enrichedNotice.books.map((book, idx) => `
              <div style="background: #FFFFFF; border: 1px solid #E5E7EB; padding: 7px; margin-bottom: 5px; border-radius: 4px;">
                <div style="font-weight: bold; font-size: 12px; margin-bottom: 3px; color: ${colorScheme.primary};">${idx + 1}. ${book.title}</div>
                <div style="font-size: 10px; color: #6B7280; margin-bottom: 2px;">Call Number: <span style="color: #374151;">${book.callNumber}</span> • Barcode: <span style="color: #374151;">${book.barcode}</span></div>
                <div style="font-size: 10px; color: #6B7280;">Due Date: <span style="color: #374151;">${enrichedNotice.bookDetails[idx].dueDate}</span> • Replacement Cost: <span style="color: #DC2626; font-weight: 600;">${book.cost}</span></div>
              </div>
            `).join('')}
          </div>
          
          ${patronType !== 'faculty' ? `
            <div style="font-size: 14px; font-weight: bold; text-align: right; margin: 10px 0; padding: 7px; background: #FEF2F2; border-radius: 6px; color: #DC2626;">
              Total Replacement Value: $${enrichedNotice.totalCost.toFixed(2)}
            </div>
          ` : ''}
          
          
          <div style="background: ${colorScheme.background}; border: 2px solid ${colorScheme.primary}; border-radius: 8px; padding: 10px; margin: 10px 0; font-size: 11px; line-height: 1.4;">
            <strong style="font-size: 12px; color: ${colorScheme.accent};">Important Information:</strong><br/>
            ${enrichedNotice.messageText}
           ${noticeTypeToUse !== 'toBeCharged' && noticeTypeToUse !== 'alreadyCharged' && noticeTypeToUse !== 'endOfYearCharge' && noticeTypeToUse !== 'endOfYearFinal' && noticeTypeToUse !== 'reminder' && patronType !== 'faculty' ? `
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${colorScheme.primary};">
                <strong>Book Details & Return Deadlines:</strong>
                <ul style="margin: 5px 0 0 18px; padding: 0;">
                  ${enrichedNotice.bookDetails.map(book => `
                    <li style="margin-bottom: 4px;">
                      <strong>${book.title}</strong><br/>
                      <span style="font-size: 10px;">${book.weeksOverdue} week${book.weeksOverdue !== 1 ? 's' : ''} overdue • Last return date before charging: <strong style="color: #DC2626;">${book.returnBy}</strong></span>
                    </li>
                  `).join('')}
                </ul>
              </div>
            ` : ''}
          </div>
          
          ${facultyPolicyHTML}
          
          ${patronType !== 'faculty' ? `
            <div style="background: ${colorScheme.background}; border: 1px solid ${colorScheme.border}; border-radius: 6px; padding: 8px; margin: 10px 0; font-size: 10px;">
              <strong style="color: ${colorScheme.accent};">📝 Payment Instructions:</strong> If a book is lost or damaged, you may return this notice with payment instructions. Payments can be made by cash, check, or through FACTS.
            </div>
          ` : ''}
          
          ${(noticeTypeToUse === 'toBeCharged' || noticeTypeToUse === 'endOfYearCharge') && patronType !== 'faculty' ? `
            <div style="background: #FEF3C7; border: 2px solid #F59E0B; border-radius: 6px; padding: 10px; margin: 10px 0; font-size: 10px;">
              <strong style="color: #92400E;">💛 If You Need Help</strong><br/>
              Every child deserves access to books, regardless of financial situation. If you're experiencing hardship, please contact Dr. Kahn confidentially at vkahn@holyfamilycs.org. We have options to help.
            </div>
          ` : ''}
          
          <div style="margin-top: 12px; padding-top: 10px; border-top: 1px solid #E5E7EB; font-size: 9px; line-height: 1.3;">
            <strong style="font-size: 10px;">Library Policy Reminders:</strong>
            <ul style="margin: 5px 0 5px 18px; padding: 0;">
              <li style="margin-bottom: 2px;">Books must be returned to the library in person</li>
              <li style="margin-bottom: 2px;">Damaged items may incur additional fees</li>
              ${patronType !== 'faculty' ? '<li style="margin-bottom: 2px;">Lost item charges are non-refundable once processed</li>' : ''}
            </ul>
            <div style="margin-top: 3px; padding: 5px; background: #F3F4F6; border-radius: 4px;">
              <strong>Questions?</strong> Contact Dr. Kahn at <a href="mailto:vkahn@holyfamilycs.org" style="color: ${colorScheme.primary}; text-decoration: none;">vkahn@holyfamilycs.org</a>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const generateAllNoticesHTML = (notices) => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>HFCS Library Overdue Notices - ${new Date().toLocaleDateString()}</title>
          <style>
            @page { 
              margin: 0.5in; 
              size: letter;
            }
            @media print {
              body { margin: 0; }
              header, footer { display: none; }
            }
            body { margin: 0; padding: 0; }
            .notice-page { page-break-after: always; page-break-inside: avoid; }
            .notice-page:last-child { page-break-after: auto; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          </style>
        </head>
        <body>
          ${notices.map(notice => generateNoticeHTML(notice)).join('')}
        </body>
      </html>
    `;
  };

  const downloadAllNoticesInOne = () => {
    const html = generateAllNoticesHTML(selectedNotices);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `HFCS_OverdueNotices_${date}_${selectedNotices.length}notices.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const organizeNotices = (organizationType = organizeBy) => {
    const organized = {};
    
    if (organizationType === 'teacher') {
      selectedNotices.forEach(notice => {
        const key = notice.teacher || 'No Teacher';
        if (!organized[key]) organized[key] = [];
        organized[key].push(notice);
      });
    } else if (organizationType === 'grade') {
      selectedNotices.forEach(notice => {
        const key = notice.grade || 'No Grade';
        if (!organized[key]) organized[key] = [];
        organized[key].push(notice);
      });
    } else if (organizationType === 'grade-teacher') {
      selectedNotices.forEach(notice => {
        const key = `${notice.grade || 'No Grade'} - ${notice.teacher || 'No Teacher'}`;
        if (!organized[key]) organized[key] = [];
        organized[key].push(notice);
      });
    } else if (organizationType === 'patron-type') {
      selectedNotices.forEach(notice => {
        const key = notice.patronType || 'Unknown';
        if (!organized[key]) organized[key] = [];
        organized[key].push(notice);
      });
    }
    
    return organized;
  };

  const printOrganized = () => {
    const organized = organizeNotices();
    const sortedKeys = Object.keys(organized).sort();
    
    const allNotices = [];
    sortedKeys.forEach(key => {
      allNotices.push(...organized[key]);
    });
    
    const html = generateAllNoticesHTML(allNotices);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    } else {
      alert('Unable to open print window. Please allow popups for this site, or use "Download All in One File" instead.');
    }
  };

  const downloadOrganized = () => {
    const organized = organizeNotices();
    const sortedKeys = Object.keys(organized).sort();
    
    const allNotices = [];
    sortedKeys.forEach(key => {
      allNotices.push(...organized[key]);
    });
    
    const html = generateAllNoticesHTML(allNotices);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    const orgLabel = organizeBy === 'teacher' ? 'ByTeacher' : organizeBy === 'grade' ? 'ByGrade' : organizeBy === 'patron-type' ? 'ByPatronType' : 'ByGradeTeacher';
    a.href = url;
    a.download = `HFCS_OverdueNotices_${orgLabel}_${date}_${allNotices.length}notices.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadNotice = (notice) => {
    const html = `<!DOCTYPE html><html><head><title>Overdue Notice - ${notice.cleanName}</title><style>@page { margin: 0.5in; size: letter; } @media print { body { margin: 0; } header, footer { display: none; } } body { margin: 0; padding: 0; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }</style></head><body>${generateNoticeHTML(notice)}</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    const safeName = notice.cleanName.replace(/[^a-zA-Z0-9]/g, '_');
    const safeType = notice.suggestedNoticeType;
    a.href = url;
    a.download = `OverdueNotice_${safeName}_${safeType}_${date}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printNotice = (notice) => {
    const html = `<!DOCTYPE html><html><head><title>Overdue Notice - ${notice.cleanName}</title><style>@page { margin: 0.5in; size: letter; } @media print { body { margin: 0; } header, footer { display: none; } } body { margin: 0; padding: 0; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }</style></head><body>${generateNoticeHTML(notice)}</body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    } else {
      alert('Unable to open print window. Please allow popups for this site, or use the download option instead.');
    }
  };

  const printAllNotices = () => {
    const html = generateAllNoticesHTML(selectedNotices);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    } else {
      alert('Unable to open print window. Please allow popups for this site, or use "Download All in One File" instead.');
    }
  };

  const copyChargeList = (patronType, noticeType) => {
    let list = [];
    
    if (patronType === 'student') {
      list = noticeBreakdown.student[noticeType] || [];
    } else if (patronType === 'parent') {
      list = noticeBreakdown.parent[noticeType] || [];
    } else if (patronType === 'faculty') {
      list = noticeBreakdown.faculty[noticeType] || [];
    }
    
    const sortedList = [...list].sort((a, b) => 
      a.cleanName.localeCompare(b.cleanName)
    );
    
    const text = sortedList.map(notice => 
      `${notice.cleanName} - $${notice.totalCost.toFixed(2)}`
    ).join('\n');
    
    navigator.clipboard.writeText(text).then(() => {
      alert(`${patronType.toUpperCase()} ${noticeType.toUpperCase()} list copied to clipboard!`);
    });
  };

  // Toggle exclusion from final notice list
  const toggleExcludeFromFinal = (cleanName) => {
    setExcludedFromFinal(prev => {
      const next = new Set(prev);
      if (next.has(cleanName)) {
        next.delete(cleanName);
      } else {
        next.add(cleanName);
      }
      return next;
    });
  };

  // Toggle exclusion from charge notice list
  const toggleExcludeFromCharge = (cleanName) => {
    setExcludedFromCharge(prev => {
      const next = new Set(prev);
      if (next.has(cleanName)) {
        next.delete(cleanName);
      } else {
        next.add(cleanName);
      }
      return next;
    });
  };

  // Record Final Notices as sent to Firebase
  const handleRecordFinalNotices = async () => {
    const finalNotices = [
      ...noticeBreakdown.student.final,
      ...noticeBreakdown.parent.final
    ].filter(n => !excludedFromFinal.has(n.cleanName));

    if (finalNotices.length === 0) {
      alert('No final notices to record');
      return;
    }

    setNoticeActionLoading(true);
    try {
      for (const notice of finalNotices) {
        await recordNoticeSent(
          currentUser?.uid,
          notice.cleanName,
          notice.patronType,
          'final',
          notice.books.map(b => ({
            title: b.title,
            barcode: b.barcode,
            dueDate: b.dueDate
          }))
        );
      }

      alert(`Recorded ${finalNotices.length} Final Notice(s) to system!`);
      setFinalNoticeRecorded(true);
    } catch (err) {
      console.error('Error recording final notices:', err);
      alert('Failed to record notices. Please try again.');
    } finally {
      setNoticeActionLoading(false);
    }
  };

  // Create charge records in Firebase for "Ready for Charge" patrons
  const handleRecordCharges = async () => {
    const toCharge = [
      ...noticeBreakdown.student.toBeCharged,
      ...noticeBreakdown.parent.toBeCharged
    ].filter(n => !excludedFromCharge.has(n.cleanName));

    if (toCharge.length === 0) {
      alert('No charges to record');
      return;
    }

    setChargeActionLoading(true);
    try {
      for (const notice of toCharge) {
        await createCharge(currentUser?.uid, {
          patronName: notice.cleanName,
          patronType: notice.patronType,
          books: notice.books.map(b => ({
            title: b.title,
            barcode: b.barcode,
            cost: parseFloat(b.cost.replace('$', '').replace(',', '')) || 0,
            dueDate: b.dueDate
          })),
          totalCost: notice.totalCost,
          notes: `Charged via Odin's Ravens on ${new Date().toLocaleDateString()}`
        });
      }

      alert(`Recorded ${toCharge.length} Charge(s) to system!`);
      setChargesRecorded(true);

      // Refresh the Firebase charges list
      await loadFirebaseCharges();

      // Move to awaiting payment
      setNoticeBreakdown(prev => ({
        ...prev,
        student: {
          ...prev.student,
          readyForCharge: [],
          awaitingPayment: [...prev.student.alreadyCharged, ...toCharge.filter(n => n.patronType === 'student')]
        },
        parent: {
          ...prev.parent,
          readyForCharge: [],
          awaitingPayment: [...prev.parent.alreadyCharged, ...toCharge.filter(n => n.patronType === 'parent')]
        }
      }));

      // Remove from selected notices
      setSelectedNotices(prev => prev.filter(n => n.category !== 'readyForCharge'));

    } catch (err) {
      console.error('Error creating charges:', err);
      alert('Failed to create charge records. Please try again.');
    } finally {
      setChargeActionLoading(false);
    }
  };

  // Mark a patron as paid - clears charge and notice history so they start fresh
  const handleMarkAsPaid = async (notice) => {
    if (!notice.pendingCharge?.id) {
      alert('No charge record found for this patron');
      return;
    }

    const confirmPaid = window.confirm(
      `Mark ${notice.cleanName} as PAID?\n\nThis will:\n• Mark their $${notice.totalCost.toFixed(2)} charge as paid\n• Clear their notice history\n\nThey will start fresh for any future overdue books.`
    );

    if (!confirmPaid) return;

    try {
      // Mark charge as paid
      await updateChargeStatus(currentUser?.uid, notice.pendingCharge.id, 'paid');

      // Clear their notice history so they start fresh
      await clearNoticeHistory(currentUser?.uid, notice.cleanName);

      // Remove from breakdown
      setNoticeBreakdown(prev => ({
        ...prev,
        student: {
          ...prev.student,
          alreadyCharged: prev.student.alreadyCharged.filter(n => n.cleanName !== notice.cleanName)
        },
        parent: {
          ...prev.parent,
          alreadyCharged: prev.parent.alreadyCharged.filter(n => n.cleanName !== notice.cleanName)
        }
      }));

      // Remove from firebaseCharges state
      setFirebaseCharges(prev => prev.filter(c => c.id !== notice.pendingCharge.id));

      alert(`${notice.cleanName} marked as paid. They will start fresh for future overdue books.`);
    } catch (err) {
      console.error('Error marking as paid:', err);
      alert('Failed to mark as paid. Please try again.');
    }
  };

  const getNoticeTypeColor = (type, patronType) => {
    if (patronType === 'faculty') {
      return 'text-yellow-600 bg-yellow-50';
    }
    
    switch(type) {
      case 'final': return 'text-red-600 bg-red-50';
      case 'toBeCharged': return 'text-red-800 bg-red-100';
      case 'alreadyCharged': return 'text-gray-700 bg-gray-100';
      case 'reminder': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-orange-600 bg-orange-50';
    }
  };

  const getNoticeTypeLabel = (type, patronType) => {
    if (patronType === 'faculty') {
      return 'Reminder (8+ wks)';
    } else if (patronType === 'parent') {
      switch(type) {
        case 'final': return 'Final Notice (13 wks)';
        case 'toBeCharged': return 'Charge Notice (14 wks)';
        case 'alreadyCharged': return 'Already Charged (15+ wks)';
        default: return 'Overdue Notice (4-12 wks)';
      }
    } else {
      switch(type) {
        case 'final': return 'Final Notice (10 wks)';
        case 'toBeCharged': return 'Charge Notice (11 wks)';
        case 'alreadyCharged': return 'Already Charged (12+ wks)';
        default: return 'Overdue Notice';
      }
    }
  };

  const getPatronIcon = (patronType) => {
    switch(patronType) {
      case 'parent': return <Users className="text-blue-500" size={16} />;
      case 'faculty': return <GraduationCap className="text-yellow-600" size={16} />;
      default: return <BookOpen className="text-green-500" size={16} />;
    }
  };

  const getUniqueTeachers = () => {
    const teachers = new Set(parsedNotices.map(n => n.teacher).filter(t => t));
    return Array.from(teachers).sort();
  };

  const filteredNotices = (() => {
    let filtered = selectedNotices;
    
    if (patronFilter !== 'all') {
      filtered = filtered.filter(n => n.patronType === patronFilter);
    }
    
    if (selectedTeacher !== 'all') {
      filtered = filtered.filter(n => n.teacher === selectedTeacher);
    }
    
    return filtered;
  })();

  const uniquePatrons = new Set(parsedNotices.map(n => n.cleanName));
  const uniqueUnmatchedPatrons = new Set(unmatchedPatrons.map(n => n.cleanName));

  const getTotalActiveNotices = () => {
    return noticeBreakdown.student.final.length +
           noticeBreakdown.student.toBeCharged.length +
           noticeBreakdown.parent.final.length +
           noticeBreakdown.parent.toBeCharged.length +
           noticeBreakdown.faculty.reminder.length;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // Show loading state while roster loads
  if (rosterLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-purple-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading roster data...</p>
      </div>
    );
  }

  // Show error state if roster failed to load
  if (rosterError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <p className="text-red-600 mb-4">{rosterError}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">Odin's Ravens</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="hidden md:block text-right">
                <p className="text-sm text-purple-300">Active Notices</p>
                <p className="text-xl font-bold">{getTotalActiveNotices()}</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-slate-200 border-b border-slate-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h2 className="text-xl font-bold text-slate-800">🦅 Odin's Ravens</h2>
          <p className="text-slate-600 mt-1">The ultimate thought and memory for library notices</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FileText className="mr-2 text-purple-600" size={24} />
                Step 1: Paste Overdue Notices Text
              </h2>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="Paste the overdue notices text here..."
                className="w-full h-48 p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />

              {/* Roster status warnings */}
              {rosterLoading && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center text-blue-700">
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Loading roster data...
                </div>
              )}
              {rosterError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="inline mr-2" size={18} />
                  {rosterError}
                </div>
              )}
              {rosterIsEmpty && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
                  <AlertCircle className="inline mr-2" size={18} />
                  No roster data found. Please import your roster in <a href="/urds-well" className="underline font-medium">Urd's Well</a> first.
                </div>
              )}

              <button
                onClick={parseOverdueText}
                disabled={parseLoading || rosterLoading || rosterIsEmpty}
                className={`mt-4 w-full py-3 px-4 rounded-lg transition font-medium flex items-center justify-center ${
                  parseLoading || rosterLoading || rosterIsEmpty
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {parseLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Parsing & Analyzing...
                  </>
                ) : (
                  'Parse & Analyze Notices'
                )}
              </button>
            </div>

            {parsedNotices.length > 0 && (
              <>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <button
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    className="w-full flex items-center justify-between text-xl font-bold text-gray-800 hover:text-purple-600 transition-colors"
                  >
                    <div className="flex items-center">
                      <Clock className="mr-2 text-blue-600" size={24} />
                      Notice Breakdown by Patron Type
                    </div>
                    <span className="text-2xl">{showBreakdown ? '−' : '+'}</span>
                  </button>
                  
                  {showBreakdown && (
                    <div className="mt-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <BookOpen className="mr-2 text-green-500" size={20} />
                          Students
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-orange-600">{noticeBreakdown.student.final.length}</div>
                            <div className="text-xs text-orange-800">Final Notices (first warning)</div>
                          </div>
                          <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-red-800">{noticeBreakdown.student.toBeCharged.length}</div>
                            <div className="text-xs text-red-900">Charge Notices (previously warned)</div>
                          </div>
                          <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-purple-700 flex items-center justify-center">
                              <Database size={16} className="mr-1" />
                              {noticeBreakdown.student.alreadyCharged.length}
                            </div>
                            <div className="text-xs text-purple-800">Awaiting Payment</div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <Users className="mr-2 text-blue-500" size={20} />
                          Parents
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-blue-600">{noticeBreakdown.parent.final.length}</div>
                            <div className="text-xs text-blue-800">Final Notices (first warning)</div>
                          </div>
                          <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-red-800">{noticeBreakdown.parent.toBeCharged.length}</div>
                            <div className="text-xs text-red-900">Charge Notices (previously warned)</div>
                          </div>
                          <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-purple-700 flex items-center justify-center">
                              <Database size={16} className="mr-1" />
                              {noticeBreakdown.parent.alreadyCharged.length}
                            </div>
                            <div className="text-xs text-purple-800">Awaiting Payment</div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <GraduationCap className="mr-2 text-yellow-600" size={20} />
                          Faculty (Never Charged)
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-yellow-600">{noticeBreakdown.faculty.reminder.length}</div>
                            <div className="text-xs text-yellow-800">Reminder (8+ wks) - Contact to return/renew</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filter by Patron Type
                      </label>
                      <select
                        value={patronFilter}
                        onChange={(e) => setPatronFilter(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="all">All Patrons ({getTotalActiveNotices()})</option>
                        <option value="student">Students ({noticeBreakdown.student.final.length + noticeBreakdown.student.toBeCharged.length})</option>
                        <option value="parent">Parents ({noticeBreakdown.parent.final.length + noticeBreakdown.parent.toBeCharged.length})</option>
                        <option value="faculty">Faculty ({noticeBreakdown.faculty.reminder.length})</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filter by Teacher
                      </label>
                      <select
                        value={selectedTeacher}
                        onChange={(e) => setSelectedTeacher(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="all">All Teachers</option>
                        {getUniqueTeachers().map(teacher => (
                          <option key={teacher} value={teacher}>{teacher}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Final Notices Section */}
                {(noticeBreakdown.student.final.length > 0 ||
                  noticeBreakdown.parent.final.length > 0) && (
                  <div className="bg-orange-50 border-2 border-orange-400 rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-bold text-orange-900 mb-4 flex items-center">
                      <AlertCircle className="mr-2 text-orange-700" size={24} />
                      Final Notices (First Warning)
                    </h2>
                    <p className="text-sm text-orange-700 mb-4">These patrons have NOT been warned before. Send final notice first.</p>

                    {noticeBreakdown.student.final.length > 0 && (
                      <div className="mb-4">
                        <h3 className="font-bold text-orange-900 mb-2">
                          Students - {noticeBreakdown.student.final.filter(n => !excludedFromFinal.has(n.cleanName)).length} notices
                          {excludedFromFinal.size > 0 && noticeBreakdown.student.final.some(n => excludedFromFinal.has(n.cleanName)) && (
                            <span className="text-orange-500 font-normal ml-2">
                              ({noticeBreakdown.student.final.filter(n => excludedFromFinal.has(n.cleanName)).length} excluded)
                            </span>
                          )}
                        </h3>
                        <div className="bg-white rounded p-2 max-h-40 overflow-y-auto text-xs">
                          {[...noticeBreakdown.student.final]
                            .sort((a, b) => a.cleanName.localeCompare(b.cleanName))
                            .map((n, i) => {
                              const isExcluded = excludedFromFinal.has(n.cleanName);
                              return (
                                <div key={i} className={`py-1 border-b last:border-0 flex justify-between items-center ${isExcluded ? 'opacity-50 bg-gray-100' : ''}`}>
                                  <span className={isExcluded ? 'line-through' : ''}>{n.cleanName}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">{n.maxWeeksOverdue} wks</span>
                                    <button
                                      onClick={() => toggleExcludeFromFinal(n.cleanName)}
                                      className={`p-1 rounded ${isExcluded ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                                      title={isExcluded ? 'Include in notices' : 'Exclude from notices'}
                                    >
                                      {isExcluded ? <RotateCcw size={14} /> : <X size={14} />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {noticeBreakdown.parent.final.length > 0 && (
                      <div className="mb-4">
                        <h3 className="font-bold text-orange-900 mb-2">
                          Parents - {noticeBreakdown.parent.final.filter(n => !excludedFromFinal.has(n.cleanName)).length} notices
                          {excludedFromFinal.size > 0 && noticeBreakdown.parent.final.some(n => excludedFromFinal.has(n.cleanName)) && (
                            <span className="text-orange-500 font-normal ml-2">
                              ({noticeBreakdown.parent.final.filter(n => excludedFromFinal.has(n.cleanName)).length} excluded)
                            </span>
                          )}
                        </h3>
                        <div className="bg-white rounded p-2 max-h-40 overflow-y-auto text-xs">
                          {[...noticeBreakdown.parent.final]
                            .sort((a, b) => a.cleanName.localeCompare(b.cleanName))
                            .map((n, i) => {
                              const isExcluded = excludedFromFinal.has(n.cleanName);
                              return (
                                <div key={i} className={`py-1 border-b last:border-0 flex justify-between items-center ${isExcluded ? 'opacity-50 bg-gray-100' : ''}`}>
                                  <span className={isExcluded ? 'line-through' : ''}>{n.cleanName}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">{n.maxWeeksOverdue} wks</span>
                                    <button
                                      onClick={() => toggleExcludeFromFinal(n.cleanName)}
                                      className={`p-1 rounded ${isExcluded ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                                      title={isExcluded ? 'Include in notices' : 'Exclude from notices'}
                                    >
                                      {isExcluded ? <RotateCcw size={14} /> : <X size={14} />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-orange-300">
                      <button
                        onClick={handleRecordFinalNotices}
                        disabled={noticeActionLoading || finalNoticeRecorded || [...noticeBreakdown.student.final, ...noticeBreakdown.parent.final].filter(n => !excludedFromFinal.has(n.cleanName)).length === 0}
                        className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 flex items-center justify-center disabled:opacity-50"
                      >
                        {noticeActionLoading ? (
                          <Loader2 className="mr-2 animate-spin" size={16} />
                        ) : finalNoticeRecorded ? (
                          <CheckCircle className="mr-2" size={16} />
                        ) : (
                          <Database className="mr-2" size={16} />
                        )}
                        {finalNoticeRecorded
                          ? 'Final Notices Recorded'
                          : `Record ${[...noticeBreakdown.student.final, ...noticeBreakdown.parent.final].filter(n => !excludedFromFinal.has(n.cleanName)).length} Final Notice(s) as Sent`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Charge Notices Section */}
                {(noticeBreakdown.student.toBeCharged.length > 0 ||
                  noticeBreakdown.parent.toBeCharged.length > 0) && (
                  <div className="bg-red-50 border-2 border-red-400 rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-bold text-red-900 mb-4 flex items-center">
                      <DollarSign className="mr-2 text-red-700" size={24} />
                      Charge Notices (Previously Warned)
                    </h2>
                    <p className="text-sm text-red-700 mb-4">These patrons have received a final notice. Ready to charge.</p>

                    {noticeBreakdown.student.toBeCharged.length > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-bold text-red-900">
                            Students - {noticeBreakdown.student.toBeCharged.filter(n => !excludedFromCharge.has(n.cleanName)).length} notices
                            {excludedFromCharge.size > 0 && noticeBreakdown.student.toBeCharged.some(n => excludedFromCharge.has(n.cleanName)) && (
                              <span className="text-red-400 font-normal ml-2">
                                ({noticeBreakdown.student.toBeCharged.filter(n => excludedFromCharge.has(n.cleanName)).length} excluded)
                              </span>
                            )}
                          </h3>
                          <button
                            onClick={() => copyChargeList('student', 'readyForCharge')}
                            className="bg-red-700 text-white py-1 px-3 rounded text-sm hover:bg-red-800 flex items-center"
                          >
                            <Copy className="mr-1" size={14} />
                            Copy
                          </button>
                        </div>
                        <div className="bg-white rounded p-2 max-h-40 overflow-y-auto text-xs">
                          {[...noticeBreakdown.student.toBeCharged]
                            .sort((a, b) => a.cleanName.localeCompare(b.cleanName))
                            .map((n, i) => {
                              const isExcluded = excludedFromCharge.has(n.cleanName);
                              return (
                                <div key={i} className={`py-1 border-b last:border-0 flex justify-between items-center ${isExcluded ? 'opacity-50 bg-gray-100' : ''}`}>
                                  <span className={isExcluded ? 'line-through' : ''}>{n.cleanName}</span>
                                  <div className="flex items-center gap-2">
                                    <span className={`font-semibold ${isExcluded ? 'line-through' : ''}`}>${n.totalCost.toFixed(2)}</span>
                                    <button
                                      onClick={() => toggleExcludeFromCharge(n.cleanName)}
                                      className={`p-1 rounded ${isExcluded ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                                      title={isExcluded ? 'Include in charges' : 'Exclude from charges'}
                                    >
                                      {isExcluded ? <RotateCcw size={14} /> : <X size={14} />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {noticeBreakdown.parent.toBeCharged.length > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-bold text-red-900">
                            Parents - {noticeBreakdown.parent.toBeCharged.filter(n => !excludedFromCharge.has(n.cleanName)).length} notices
                            {excludedFromCharge.size > 0 && noticeBreakdown.parent.toBeCharged.some(n => excludedFromCharge.has(n.cleanName)) && (
                              <span className="text-red-400 font-normal ml-2">
                                ({noticeBreakdown.parent.toBeCharged.filter(n => excludedFromCharge.has(n.cleanName)).length} excluded)
                              </span>
                            )}
                          </h3>
                          <button
                            onClick={() => copyChargeList('parent', 'readyForCharge')}
                            className="bg-red-700 text-white py-1 px-3 rounded text-sm hover:bg-red-800 flex items-center"
                          >
                            <Copy className="mr-1" size={14} />
                            Copy
                          </button>
                        </div>
                        <div className="bg-white rounded p-2 max-h-40 overflow-y-auto text-xs">
                          {[...noticeBreakdown.parent.toBeCharged]
                            .sort((a, b) => a.cleanName.localeCompare(b.cleanName))
                            .map((n, i) => {
                              const isExcluded = excludedFromCharge.has(n.cleanName);
                              return (
                                <div key={i} className={`py-1 border-b last:border-0 flex justify-between items-center ${isExcluded ? 'opacity-50 bg-gray-100' : ''}`}>
                                  <span className={isExcluded ? 'line-through' : ''}>{n.cleanName}</span>
                                  <div className="flex items-center gap-2">
                                    <span className={`font-semibold ${isExcluded ? 'line-through' : ''}`}>${n.totalCost.toFixed(2)}</span>
                                    <button
                                      onClick={() => toggleExcludeFromCharge(n.cleanName)}
                                      className={`p-1 rounded ${isExcluded ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                                      title={isExcluded ? 'Include in charges' : 'Exclude from charges'}
                                    >
                                      {isExcluded ? <RotateCcw size={14} /> : <X size={14} />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-red-300">
                      <button
                        onClick={handleRecordCharges}
                        disabled={chargeActionLoading || chargesRecorded || [...noticeBreakdown.student.toBeCharged, ...noticeBreakdown.parent.toBeCharged].filter(n => !excludedFromCharge.has(n.cleanName)).length === 0}
                        className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 flex items-center justify-center disabled:opacity-50"
                      >
                        {chargeActionLoading ? (
                          <Loader2 className="mr-2 animate-spin" size={16} />
                        ) : chargesRecorded ? (
                          <CheckCircle className="mr-2" size={16} />
                        ) : (
                          <CheckSquare className="mr-2" size={16} />
                        )}
                        {chargesRecorded
                          ? 'Charges Recorded to System'
                          : `Record ${[...noticeBreakdown.student.toBeCharged, ...noticeBreakdown.parent.toBeCharged].filter(n => !excludedFromCharge.has(n.cleanName)).length} Charge(s) to System`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Awaiting Payment Section */}
                {(noticeBreakdown.student.alreadyCharged.length > 0 ||
                  noticeBreakdown.parent.alreadyCharged.length > 0) && (
                  <div className="bg-purple-50 border-2 border-purple-400 rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-bold text-purple-900 mb-4 flex items-center">
                      <Database className="mr-2 text-purple-700" size={24} />
                      Awaiting Payment (Already in System)
                    </h2>
                    <p className="text-sm text-purple-700 mb-4">These patrons have pending charges. Click "Paid" when payment is received to clear their record.</p>

                    {noticeBreakdown.student.alreadyCharged.length > 0 && (
                      <div className="mb-4">
                        <h3 className="font-bold text-purple-900 mb-2">Students - {noticeBreakdown.student.alreadyCharged.length}</h3>
                        <div className="bg-white rounded p-2 max-h-48 overflow-y-auto text-sm">
                          {[...noticeBreakdown.student.alreadyCharged]
                            .sort((a, b) => a.cleanName.localeCompare(b.cleanName))
                            .map((n, i) => (
                              <div key={i} className="py-2 border-b last:border-0 flex items-center justify-between">
                                <span>{n.cleanName} - <strong>${n.totalCost.toFixed(2)}</strong></span>
                                <button
                                  onClick={() => handleMarkAsPaid(n)}
                                  className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition flex items-center"
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Paid
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {noticeBreakdown.parent.alreadyCharged.length > 0 && (
                      <div>
                        <h3 className="font-bold text-purple-900 mb-2">Parents - {noticeBreakdown.parent.alreadyCharged.length}</h3>
                        <div className="bg-white rounded p-2 max-h-48 overflow-y-auto text-sm">
                          {[...noticeBreakdown.parent.alreadyCharged]
                            .sort((a, b) => a.cleanName.localeCompare(b.cleanName))
                            .map((n, i) => (
                              <div key={i} className="py-2 border-b last:border-0 flex items-center justify-between">
                                <span>{n.cleanName} - <strong>${n.totalCost.toFixed(2)}</strong></span>
                                <button
                                  onClick={() => handleMarkAsPaid(n)}
                                  className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition flex items-center"
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Paid
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <Users className="mr-2 text-green-600" size={24} />
                    Active Notices to Generate ({filteredNotices.length})
                  </h2>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredNotices.map((notice, idx) => (
                      <div key={notice.noticeId} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  setSelectedNotices(selectedNotices.filter(n => n.noticeId !== notice.noticeId));
                                }
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {getPatronIcon(notice.patronType)}
                                <span className="font-semibold">{notice.cleanName}</span>
                                <CheckCircle className="text-green-500" size={16} />
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getNoticeTypeColor(notice.suggestedNoticeType, notice.patronType)}`}>
                                  {getNoticeTypeLabel(notice.suggestedNoticeType, notice.patronType)}
                                </span>
                              </div>
                              {notice.patronType === 'parent' && notice.children && (
                                <div className="text-xs text-blue-600 mt-1">
                                  Children: {notice.children.join(', ')}
                                </div>
                              )}
                              <div className="text-sm text-gray-600 mt-1">
                                {notice.patronType === 'faculty' ? 
                                  `Grade/Dept: ${notice.grade || 'N/A'}` :
                                  `Teacher: ${notice.teacher} | Grade: ${notice.grade}`
                                }
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                {notice.books.length} book(s) • ${notice.totalCost.toFixed(2)}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => downloadNotice(notice)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>
                            <button
                              onClick={() => printNotice(notice)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title="Print"
                            >
                              <Printer size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {unmatchedPatrons.length > 0 && (
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-bold text-yellow-900 mb-4 flex items-center">
                      <AlertCircle className="mr-2 text-yellow-600" size={24} />
                      Patrons Not Found ({uniqueUnmatchedPatrons.size} patrons, {unmatchedPatrons.length} notices)
                    </h2>
                    <p className="text-sm text-yellow-800 mb-4">
                      These patrons were not found in the roster. Manually assign teacher/grade or delete.
                    </p>
                    
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {unmatchedPatrons.map((notice, idx) => (
                        <div key={notice.noticeId} className="bg-white border border-yellow-200 rounded-lg p-4">
                          {editingPatron?.noticeId === notice.noticeId ? (
                            <div className="space-y-3">
                              <div>
                                {getPatronIcon(notice.patronType)}
                                <span className="ml-2 font-semibold text-gray-800">{notice.cleanName}</span>
                                <span className={`ml-2 text-xs px-2 py-1 rounded-full font-medium ${getNoticeTypeColor(notice.suggestedNoticeType, notice.patronType)}`}>
                                  {getNoticeTypeLabel(notice.suggestedNoticeType, notice.patronType)}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  placeholder="Teacher name"
                                  defaultValue={editingPatron.teacher}
                                  onChange={(e) => setEditingPatron({...editingPatron, teacher: e.target.value})}
                                  className="p-2 border border-gray-300 rounded"
                                />
                                <input
                                  type="text"
                                  placeholder="Grade"
                                  defaultValue={editingPatron.grade}
                                  onChange={(e) => setEditingPatron({...editingPatron, grade: e.target.value})}
                                  className="p-2 border border-gray-300 rounded"
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => savePatronEdit(notice, editingPatron.teacher, editingPatron.grade)}
                                  className="flex-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 flex items-center justify-center"
                                >
                                  <Save size={16} className="mr-1" /> Save
                                </button>
                                <button
                                  onClick={() => setEditingPatron(null)}
                                  className="flex-1 bg-gray-400 text-white px-3 py-2 rounded hover:bg-gray-500"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div>
                                {getPatronIcon(notice.patronType)}
                                <span className="ml-2 font-semibold text-gray-800">{notice.cleanName}</span>
                                <span className={`ml-2 text-xs px-2 py-1 rounded-full font-medium ${getNoticeTypeColor(notice.suggestedNoticeType, notice.patronType)}`}>
                                  {getNoticeTypeLabel(notice.suggestedNoticeType, notice.patronType)}
                                </span>
                                <div className="text-sm text-gray-600 mt-1">
                                  {notice.books.length} book(s) • ${notice.totalCost.toFixed(2)}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingPatron(notice)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                  title="Edit"
                                >
                                  <Edit2 size={20} />
                                </button>
                                <button
                                  onClick={() => deleteUnmatchedPatron(notice)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title="Delete"
                                >
                                  <Trash2 size={20} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-6">
            {parsedNotices.length > 0 && (
              <>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Patrons:</span>
                      <span className="font-bold">{uniquePatrons.size + uniqueUnmatchedPatrons.size}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Notices:</span>
                      <span className="font-bold">{parsedNotices.length + unmatchedPatrons.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Active Notices:</span>
                      <span className="font-bold text-green-600">
                        {getTotalActiveNotices()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center">
                        <BookOpen size={14} className="mr-1 text-green-500" />
                        Students:
                      </span>
                      <span className="font-bold">
                        {noticeBreakdown.student.initial.length +
                         noticeBreakdown.student.final.length +
                         noticeBreakdown.student.toBeCharged.length +
                         noticeBreakdown.student.alreadyCharged.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center">
                        <Users size={14} className="mr-1 text-blue-500" />
                        Parents:
                      </span>
                      <span className="font-bold">
                        {noticeBreakdown.parent.initial.length +
                         noticeBreakdown.parent.final.length +
                         noticeBreakdown.parent.toBeCharged.length +
                         noticeBreakdown.parent.alreadyCharged.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center">
                        <GraduationCap size={14} className="mr-1 text-yellow-600" />
                        Faculty:
                      </span>
                      <span className="font-bold">
                        {noticeBreakdown.faculty.reminder.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Need Review:</span>
                      <span className="font-bold text-yellow-600">
                        {unmatchedPatrons.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Books:</span>
                      <span className="font-bold">
                        {parsedNotices.reduce((sum, n) => sum + n.books.length, 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Value:</span>
                      <span className="font-bold text-purple-600">
                        ${parsedNotices.reduce((sum, n) => sum + n.totalCost, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedNotices.length > 0 && (
                  <>
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-4">Download & Print</h3>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Organize By
                        </label>
                        <select
                          value={organizeBy}
                          onChange={(e) => setOrganizeBy(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                        >
                          <option value="teacher">By Teacher ({Object.keys(organizeNotices('teacher')).length} groups)</option>
                          <option value="grade">By Grade ({Object.keys(organizeNotices('grade')).length} groups)</option>
                          <option value="grade-teacher">By Grade & Teacher ({Object.keys(organizeNotices('grade-teacher')).length} groups)</option>
                          <option value="patron-type">By Patron Type ({Object.keys(organizeNotices('patron-type')).length} groups)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Sorts all notices into one organized document
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <button
                          onClick={downloadAllNoticesInOne}
                          className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 transition font-medium flex items-center justify-center"
                        >
                          <Download className="mr-2" size={20} />
                          Download All (As Listed)
                        </button>
                        
                        <button
                          onClick={downloadOrganized}
                          className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition flex items-center justify-center text-sm"
                        >
                          <Download className="mr-2" size={18} />
                          Download Organized
                        </button>
                        
                        <button
                          onClick={printAllNotices}
                          className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition font-medium flex items-center justify-center"
                        >
                          <Printer className="mr-2" size={20} />
                          Print All (As Listed)
                        </button>
                        
                        <button
                          onClick={printOrganized}
                          className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center text-sm"
                        >
                          <UserCheck className="mr-2" size={18} />
                          Print Organized
                        </button>
                      </div>
                    </div>

                    <div className="bg-amber-50 border-2 border-amber-400 rounded-lg shadow-md p-6">
                      <div className="flex items-center mb-2">
                        <AlertCircle className="mr-2 text-amber-600" size={20} />
                        <h3 className="text-lg font-bold text-amber-900">End of Year Tools</h3>
                      </div>
                      <p className="text-xs text-amber-800 mb-4">
                        Use a separate end-of-year overdue list (not the main paste above) to generate Final or Charge notices. The Charge action records charges to the system.
                      </p>
                      <button
                        onClick={() => setShowEoyModal(true)}
                        className="w-full bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition font-medium flex items-center justify-center text-sm"
                      >
                        <FileText className="mr-2" size={18} />
                        Open End of Year Paste Tool
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showEoyModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full my-8">
            <div className="sticky top-0 bg-amber-50 border-b-2 border-amber-400 px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
              <div className="flex items-center">
                <AlertCircle className="mr-2 text-amber-600" size={22} />
                <h2 className="text-xl font-bold text-amber-900">End of Year Tools</h2>
              </div>
              <button
                onClick={() => setShowEoyModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste End-of-Year Overdue List
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Tab-separated columns: <span className="font-mono">Last, First &nbsp;|&nbsp; Patron ID &nbsp;|&nbsp; (blank) &nbsp;|&nbsp; Title &nbsp;|&nbsp; Call Number &nbsp;|&nbsp; Barcode &nbsp;|&nbsp; Checkout Date &nbsp;|&nbsp; Due Date &nbsp;|&nbsp; Price</span>
                </p>
                <textarea
                  value={eoyRawText}
                  onChange={(e) => setEoyRawText(e.target.value)}
                  placeholder={'Adiputra, Evelyn\t2742\t\tWitch Hat Atelier Volume 5\tMS GN SHI\t127699\t2026-04-27\t2026-05-04\t$15.99'}
                  rows={8}
                  className="w-full p-3 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-amber-500"
                />
                <button
                  onClick={parseEoyOverdueText}
                  disabled={eoyParseLoading || !eoyRawText.trim()}
                  className="mt-3 bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition font-medium flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {eoyParseLoading ? (
                    <Loader2 className="mr-2 animate-spin" size={18} />
                  ) : (
                    <Database className="mr-2" size={18} />
                  )}
                  Parse List
                </button>
              </div>

              {eoyParsedNotices.length > 0 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Patron Type</label>
                      <select
                        value={eoyPatronFilter}
                        onChange={(e) => setEoyPatronFilter(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                      >
                        <option value="all">All Patrons</option>
                        <option value="student">Students</option>
                        <option value="parent">Parents</option>
                        <option value="faculty">Faculty</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Teacher</label>
                      <select
                        value={eoySelectedTeacher}
                        onChange={(e) => setEoySelectedTeacher(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                      >
                        <option value="all">All Teachers</option>
                        {getEoyUniqueTeachers().map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Organize By</label>
                      <select
                        value={eoyOrganizeBy}
                        onChange={(e) => setEoyOrganizeBy(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                      >
                        <option value="teacher">By Teacher ({Object.keys(getEoyOrganizedNotices('teacher')).length})</option>
                        <option value="grade">By Grade ({Object.keys(getEoyOrganizedNotices('grade')).length})</option>
                        <option value="grade-teacher">By Grade & Teacher ({Object.keys(getEoyOrganizedNotices('grade-teacher')).length})</option>
                        <option value="patron-type">By Patron Type ({Object.keys(getEoyOrganizedNotices('patron-type')).length})</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-amber-900 mb-1">
                      Final Return Date (for End of Year Final notices)
                    </label>
                    <input
                      type="date"
                      value={endOfYearDate}
                      onChange={(e) => setEndOfYearDate(e.target.value)}
                      className="w-full p-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    />
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-800">
                        Parsed Notices ({eoyFilteredNotices.length} of {eoyParsedNotices.filter(n => n.matched).length} matched
                        {eoyParsedNotices.some(n => !n.matched) && (
                          <span className="text-red-600">; {eoyParsedNotices.filter(n => !n.matched).length} unmatched</span>
                        )})
                      </h3>
                    </div>
                    <ul className="max-h-60 overflow-y-auto text-xs space-y-1">
                      {eoyFilteredNotices.map(n => (
                        <li key={n.noticeId} className="flex justify-between border-b border-gray-200 last:border-0 py-1">
                          <span className="flex items-center gap-2">
                            {getPatronIcon(n.patronType)}
                            <span>{n.cleanName}</span>
                            {n.teacher && <span className="text-gray-500">— {n.teacher}</span>}
                          </span>
                          <span className="text-gray-600">{n.books.length} item(s) · ${n.totalCost.toFixed(2)}</span>
                        </li>
                      ))}
                      {eoyParsedNotices.filter(n => !n.matched).map(n => (
                        <li key={n.noticeId} className="flex justify-between border-b border-gray-200 last:border-0 py-1 opacity-60">
                          <span className="flex items-center gap-2">
                            <AlertCircle size={14} className="text-red-500" />
                            <span>{n.cleanName}</span>
                            <span className="text-red-600">unmatched — skipped</span>
                          </span>
                          <span className="text-gray-600">{n.books.length} item(s) · ${n.totalCost.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border-2 border-red-300 bg-red-50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-red-900 mb-1 flex items-center justify-between">
                        <span>End of Year Final Notice</span>
                        {eoyFinalRecorded && (
                          <span className="flex items-center text-green-700 text-xs font-normal">
                            <CheckCircle size={14} className="mr-1" /> Recorded
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-red-800 mb-3">
                        Materials must be returned by {endOfYearDate ? new Date(endOfYearDate).toLocaleDateString() : '—'}. {eoyFinalRecorded ? 'Already recorded — re-print/download anytime.' : 'Generating writes a final notice to each patron\'s history in Firebase.'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => eoyGenerateAndOutput('endOfYearFinal', 'download')}
                          disabled={eoyActionLoading}
                          className="flex-1 bg-red-600 text-white py-2 px-3 rounded-lg hover:bg-red-700 transition flex items-center justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {eoyActionLoading ? <Loader2 className="mr-1 animate-spin" size={14} /> : <Download className="mr-1" size={14} />}
                          Download
                        </button>
                        <button
                          onClick={() => eoyGenerateAndOutput('endOfYearFinal', 'print')}
                          disabled={eoyActionLoading}
                          className="flex-1 bg-red-700 text-white py-2 px-3 rounded-lg hover:bg-red-800 transition flex items-center justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {eoyActionLoading ? <Loader2 className="mr-1 animate-spin" size={14} /> : <Printer className="mr-1" size={14} />}
                          Print
                        </button>
                      </div>
                    </div>

                    <div className="border-2 border-red-400 bg-red-100 rounded-lg p-4">
                      <div className="text-sm font-semibold text-red-900 mb-1 flex items-center justify-between">
                        <span>End of Year Charge Notice</span>
                        {eoyChargesRecorded && (
                          <span className="flex items-center text-green-700 text-xs font-normal">
                            <CheckCircle size={14} className="mr-1" /> Recorded
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-red-800 mb-3">
                        {eoyChargesRecorded ? 'Charges already recorded — re-print/download anytime.' : 'Generating creates pending charges in Firebase for each non-faculty patron.'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => eoyGenerateAndOutput('endOfYearCharge', 'download')}
                          disabled={eoyActionLoading}
                          className="flex-1 bg-red-800 text-white py-2 px-3 rounded-lg hover:bg-red-900 transition flex items-center justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {eoyActionLoading ? <Loader2 className="mr-1 animate-spin" size={14} /> : <Download className="mr-1" size={14} />}
                          Download
                        </button>
                        <button
                          onClick={() => eoyGenerateAndOutput('endOfYearCharge', 'print')}
                          disabled={eoyActionLoading}
                          className="flex-1 bg-red-900 text-white py-2 px-3 rounded-lg hover:bg-red-950 transition flex items-center justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {eoyActionLoading ? <Loader2 className="mr-1 animate-spin" size={14} /> : <Printer className="mr-1" size={14} />}
                          Print
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OdinsRavens;